import customTheme from '@/constants/theme.custom';
import { Divider } from '@chakra-ui/react';
import { Box, Button, IconAlertCircle, Input, Text } from '@cmpsr/components';
import MembershipBenefits from '../../shared/MembershipBenefits';

interface MembershipSummaryProps {
  priceData: any;
  isCouponVisible: boolean;
  showCoupon: () => void;
  coupon: string;
  setCoupon: (coupon: string) => void;
  couponValidationError: string | false | null;
  isSubmitting: boolean;
  getPrice: () => void;
}

const MembershipSummary = ({
  priceData,
  isCouponVisible,
  showCoupon,
  coupon,
  setCoupon,
  couponValidationError,
  getPrice,
}: MembershipSummaryProps) => {
  const isLoading = !priceData;
  const totalPrice = priceData?.summary?.amount !== null ? (priceData.summary.amount / 100).toFixed(0) : null;

  return (
    <Box p="15px" bg="#F0F5F6" borderRadius="md" width="100%" gap="15px" display="flex" flexDirection="column">
      <Box display="flex" justifyContent="space-between">
        <Text
          fontWeight={500}
          fontSize="14px"
          fontFamily={customTheme.typography.fonts.inter}
          textTransform="uppercase"
          letterSpacing="0.1em"
        >
          {'Membership Benefits'}
        </Text>
      </Box>
      <MembershipBenefits />
      <Divider borderColor="med-blue" py={1} />

      <Box display="flex" flexDirection="column" gap="15px">
        <Box>
          {isCouponVisible ? (
            <>
              <Text
                fontSize="12px"
                fontWeight={500}
                fontFamily={customTheme.typography.fonts.inter}
                textTransform="uppercase"
              >
                PROMO CODE
              </Text>
              <Box display="flex">
                <Input
                  flex={1}
                  placeholder="Enter promo code"
                  value={coupon}
                  onChange={(e) => setCoupon(e.target.value)}
                  size="s"
                  borderColor={couponValidationError ? 'salmon-light' : undefined}
                  backgroundRepeat="no-repeat"
                  backgroundPosition="95% 50%"
                  backgroundImage={
                    couponValidationError
                      ? '/images/icons/wrong.svg'
                      : couponValidationError === false
                      ? '/images/icons/right.svg'
                      : 'none'
                  }
                />
                <Button size="s" ml={2} onClick={getPrice} isDisabled={!!couponValidationError || !coupon}>
                  Enter
                </Button>
              </Box>
              {!!couponValidationError && (
                <Box display="flex" alignItems="center">
                  <IconAlertCircle color="salmon-light" />
                  <Text ml={2} fontSize="12px" color="salmon-light">
                    {couponValidationError}
                  </Text>
                </Box>
              )}
            </>
          ) : (
            <Box display="flex">
              <Text fontSize="12px" fontFamily={customTheme.typography.fonts.inter}>
                Have a Promo code?
              </Text>
              <Text
                as="span"
                fontWeight="bold"
                textDecoration="underline"
                cursor="pointer"
                fontSize="12px"
                ml={1}
                onClick={showCoupon}
              >
                Add Here
              </Text>
            </Box>
          )}
        </Box>

        {priceData?.lines?.map((line: any, index: number) => (
          <Box key={index} display="flex" justifyContent="space-between">
            <Text fontSize="14px" fontWeight="400" fontFamily={customTheme.typography.fonts.inter}>
              {line.description}
            </Text>
            <Text fontSize="14px" fontFamily={customTheme.typography.fonts.inter}>
              {isLoading ? (
                <Box as="span" opacity={0.7}>
                  Loading...
                </Box>
              ) : line.amount ? (
                `${line.description === 'DISCOUNT' ? '-' : ''}$${(line.amount / 100).toFixed(0)}`
              ) : (
                <Box as="span" opacity={0.7}>
                  Calculating...
                </Box>
              )}
            </Text>
          </Box>
        ))}

        <Box display="flex" justifyContent="space-between">
          <Text
            fontSize="14px"
            fontWeight="500"
            fontFamily={customTheme.typography.fonts.inter}
            textTransform="uppercase"
          >
            Total
          </Text>
          <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0}>
            <Text fontSize="14px" fontFamily={customTheme.typography.fonts.inter} fontWeight="500">
              {isLoading ? (
                <Box as="span" opacity={0.7}>
                  Loading...
                </Box>
              ) : totalPrice ? (
                `$${totalPrice}`
              ) : (
                <Box as="span" opacity={0.7}>
                  Calculating...
                </Box>
              )}
            </Text>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MembershipSummary;
